// SendHealthNotice — "sending is off" BEFORE the send, not after (Epic C4).
//
// v_send_health -> { enabled, sent_this_hour, blocked_today, waiting } (one row).
// `enabled` is FALSE by default; a watchdog trips it off on a volume spike. The
// send pipeline is BUILT (04_CONVERSATIONS.md §4.3): fn_compose_send stores the
// message as approved and a worker (the gmail-send edge function) claims +
// delivers it. So "off" does not mean the reply is lost -- it is saved as a draft
// and goes out when sending resumes. Say that BEFORE the send, in the composer,
// so nobody types a reply expecting it to land immediately.
//
// Self-contained on purpose: getSendHealth is fetched here, NOT added to the page
// Promise.all, because v_send_health currently times out (Postgres 57014 -- a
// backend bug to report, not a UI patch; docs/CLIENTHUB-RPC-GAPS.md §6). A
// timing-out health view must never block the thread/rooms load (00_CONTRACT.md
// §17 -- secondary surfaces may lag, never block). On any error the notice
// treats sending as off (the conservative default -- an unknown send state must
// never read as "on"); getSendHealth already returns { enabled:false } when the
// view is empty, so error and empty collapse to the same honest default. No
// flash while pending (renders nothing until settled).
import React, { useCallback, useEffect, useState } from "react";
import { getSendHealth } from "../../../api/conversations";

/**
 * "Sending is off" notice, rendered above <Composer> so it is seen BEFORE typing.
 * Self-contained: fetches getSendHealth() once on mount; renders nothing while
 * pending (no flash) and nothing when sending is on.
 */
export default function SendHealthNotice() {
  const [state, setState] = useState("loading"); // loading | off | on
  const [waiting, setWaiting] = useState(null);

  const check = useCallback(async () => {
    setState("loading");
    try {
      const health = await getSendHealth();
      if (health && health.enabled === true) {
        setState("on");
        setWaiting(null);
      } else {
        setState("off");
        setWaiting(
          health && typeof health.waiting === "number" ? health.waiting : null,
        );
      }
    } catch (err) {
      // Unknown send state -> conservative default: sending is off. Never "on".
      setState("off");
      setWaiting(null);
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  if (state !== "off") return null; // loading (no flash) + on (nothing to say)

  return (
    <div
      className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-800/60 dark:bg-amber-900/20"
      role="status"
      aria-live="polite"
      data-testid="send-health-notice"
    >
      <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
        Sending is off
      </p>
      <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-400">
        A watchdog paused outbound delivery. Your reply is saved as a draft and
        will go out when sending resumes
        {waiting != null ? ` · ${waiting} waiting` : ""}.
      </p>
    </div>
  );
}
